import { CancelTokenSource } from 'axios';

import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { IRequest } from '~src/httpClient/serverApi/types/IRequest';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { createCancelToken } from '~src/uploadContent/utils/UploadContentRequestHelper';

import { BeginBundleUploadRequest } from '../serverApi/requests/BeginBundleUploadRequest';
import { CancelBundleUploadRequest } from '../serverApi/requests/CancelBundleUploadRequest';
import { CancelExecutionRequest } from '../serverApi/requests/CancelExecutionRequest';
import { GetExecutionStatusRequest } from '../serverApi/requests/GetExecutionStatusRequest';
import { TriggerBundleUploadRequest } from '../serverApi/requests/TriggerBundleUploadRequest';
import { appendFileUpload, initiateFileUpload } from '../serverApi/restApi/BundleUploadRestApi';
import { BundleUploadStage, TerminalBundleUploadStage } from '../serverApi/types/IBundleUpload';
import { IExecution } from '../serverApi/types/IExecution';

/** Chunk size for the house append loop: 64 MiB, the maximum chunk the house upload clients (tabcmd, Desktop) send. */
export const CHUNK_SIZE_BYTES = 64 * 1024 * 1024;

export interface UploadBundleFileOptions {
  /** Called with 0-100 as chunks upload (aggregated across the whole file). */
  onProgress?: (percent: number) => void;
  /** Abort an in-flight upload (passed to each chunk's axios request). */
  cancelToken?: CancelTokenSource;
}

export { createCancelToken };

/**
 * Sends a Web Client request and rejects when the server answers HTTP 200 with an `errors` payload
 * (its shape for unknown exceptions, e.g. an unreachable Content Mobility service), so a failed call
 * never reads as success.
 */
function sendWebRequest<TParams, TResult extends IResult>(request: IRequest<TParams, TResult>): Promise<TResult> {
  return WebHttpClient.sendRequest(request).then(function(result) {
    return rejectIfResultContainsError(result) as TResult;
  });
}

/** Rejects when a required field is missing from an otherwise successful result. */
function requireField<T>(value: T, name: string): T | Promise<never> {
  return value ? value : Promise.reject(new Error('Missing ' + name + ' in the server response'));
}

/**
 * Reserves the bundle for a new upload (`beginUpload` -> CM `createBundle`). Called before any bytes
 * are sent; the returned bundle luid keys the trigger, cancel, and the later import.
 */
export function beginBundleUpload(): Promise<string> {
  return sendWebRequest(new BeginBundleUploadRequest())
    .then(function(reservation) { return requireField(reservation && reservation.bundleLuid, 'bundleLuid'); });
}

/**
 * Stages a bundle through the house chunked-upload: initiate once, then PUT the file in
 * CHUNK_SIZE_BYTES slices, reporting aggregated progress. Resolves with the upload session id to pass
 * to {@link triggerBundleUpload}. An empty file can never be a valid bundle, so it is rejected
 * before anything is sent.
 */
export async function uploadBundleFile(file: File, options: UploadBundleFileOptions = {}): Promise<string> {
  if (file.size === 0) {
    throw new Error('Cannot upload an empty bundle file');
  }
  const initiated = await initiateFileUpload(options.cancelToken);
  const uploadSessionId = initiated.fileUpload.uploadSessionId;

  const totalBytes = file.size;
  const chunkCount = Math.ceil(totalBytes / CHUNK_SIZE_BYTES);

  for (let index = 0; index < chunkCount; index++) {
    const start = index * CHUNK_SIZE_BYTES;
    const chunk = file.slice(start, Math.min(start + CHUNK_SIZE_BYTES, totalBytes));

    const onUploadProgress = options.onProgress
      ? function(event: { loaded?: number }): void {
          const uploadedSoFar = start + (event && event.loaded ? event.loaded : 0);
          const percent = Math.min(100, Math.round((uploadedSoFar / totalBytes) * 100));
          options.onProgress(percent);
        }
      : undefined;

    await appendFileUpload(uploadSessionId, chunk, onUploadProgress, options.cancelToken);
  }

  if (options.onProgress) {
    options.onProgress(100);
  }
  return uploadSessionId;
}

/**
 * Queues the run-now upload task for a completed upload session and its reserved bundle; resolves
 * with the background job id to poll.
 */
export function triggerBundleUpload(uploadSessionId: string, bundleLuid: string, filename?: string): Promise<number> {
  return sendWebRequest(new TriggerBundleUploadRequest({
    uploadSessionId: uploadSessionId,
    bundleLuid: bundleLuid,
    filename: filename,
  })).then(function(execution) { return requireField(execution && execution.backgroundJobId, 'backgroundJobId'); });
}

/** One status poll for the upload execution, keyed on its background job id. */
export function getUploadExecutionStatus(backgroundJobId: number): Promise<IExecution> {
  return sendWebRequest(new GetExecutionStatusRequest({ backgroundJobId: backgroundJobId }));
}

/**
 * Cancels an upload by whichever handle applies: the background job once one is linked, otherwise the
 * reserved bundle. Falls back from the job to the bundle only when the service can't resolve the job
 * yet (CONTENT_NOT_FOUND, i.e. the worker hasn't linked it to the execution); any other failure —
 * already terminal, permissions, network — is passed through rather than retried against the bundle.
 */
export function cancelBundleUpload(bundleLuid: string, backgroundJobId?: number): Promise<IExecution> {
  const cancelByBundle = function(): Promise<IExecution> {
    return sendWebRequest(new CancelBundleUploadRequest({ bundleLuid: bundleLuid }));
  };
  if (!backgroundJobId) {
    return cancelByBundle();
  }
  return sendWebRequest(new CancelExecutionRequest({ backgroundJobId: backgroundJobId }))
    .catch(function(error) {
      return error === CommonErrorCode.ContentNotFound ? cancelByBundle() : Promise.reject(error);
    });
}

/**
 * Maps an execution to the stage the dialog renders:
 *
 *   SUCCEEDED                 -> ready (validated + unpacked, ready to import)
 *   FAILED                    -> failed (invalid bundle, integrity or unpack failure)
 *   CANCELLED                 -> cancelled
 *   RUNNING                   -> processing
 *   QUEUED / anything else    -> queued (includes an upload still created / uploading)
 */
export function toUploadStage(execution: IExecution): BundleUploadStage {
  const state = (execution.state || '').toUpperCase();
  if (state === 'SUCCEEDED') {
    return 'ready';
  }
  if (state === 'FAILED') {
    return 'failed';
  }
  if (state === 'CANCELLED') {
    return 'cancelled';
  }
  if (state === 'RUNNING') {
    return 'processing';
  }
  return 'queued';
}

/** True once a stage is terminal (polling can stop). */
export function isTerminalStage(stage: BundleUploadStage): stage is TerminalBundleUploadStage {
  return stage === 'ready' || stage === 'failed' || stage === 'cancelled';
}
